import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ReferenceDataLabels } from '../../../../../lib/constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../../../../lib/constants/reference-data-option-lists';
import { UserPermissions } from '../../../../../lib/constants/user-permissions';
import { MortgageApplication } from '../../../models/mortgage-application.model';
import { OutstandingDebt } from '../../../models/outstanding-debt.model';

@Component({
  selector: 'boi-form-outgoings',
  templateUrl: './form-outgoings.component.html',
  styleUrls: ['./form-outgoings.component.scss']
})
export class FormOutgoingsComponent implements OnDestroy, OnInit, OnChanges {
  @Input() public mortgageApplication: MortgageApplication;
  @Input() public isLoanGroupTypeBtlAffordability: boolean;
  public outgoings: Array<OutstandingDebt>;
  public referenceDataLabels = ReferenceDataLabels;
  public referenceDataOptionLists = ReferenceDataOptionLists;
  public userPermissions = UserPermissions;
  public selectedOutgoingType: string;
  public cannotSelectOutgoingType: boolean;
  public hasOutgoings: boolean;
  public cannotAddOutgoing = true;
  public isTotalMonthlyRentalIncomeBtlVisible: boolean;
  public isTotalBalanceOutstandingBtlMortgageVisible: boolean;
  public areBtlAdditionalFieldsVisible: boolean;

  public addNewOutgoing(outgoingType: string): void {
    if (this.mortgageApplication.IsApplicationReadOnly || this.mortgageApplication.IsFactFindReadOnly) {
      return;
    }

    const newOutgoing = new OutstandingDebt();
    newOutgoing.OutstandingDebtID = this.getIdForUnsavedOutgoing();
    newOutgoing.DebtOutgoingTypeCode = outgoingType;
    newOutgoing.CreatedDate = new Date();
    newOutgoing.ApplicantOrderIds.push(this.mortgageApplication.Applicants[0].ApplicantTransactOrder);
    this.outgoings.push(newOutgoing);
    this.hasOutgoings = this.outgoings.length > 0;

    this.mortgageApplication.Applicants[0].OutstandingDebts.push(newOutgoing);
    this.mortgageApplication.HasOutgoingsCode = ReferenceDataOptionLists.AnswerTypeYes;
  }

  private getIdForUnsavedOutgoing(): number {
    const minId = Math.min(...this.outgoings.map((o) => o.OutstandingDebtID));

    return minId < 0 ? minId - 1 : -1;
  }

  private setBtlAdditionalFieldsVisibility(): void {
    this.areBtlAdditionalFieldsVisible = this.mortgageApplication.HowManyBtlWithUs > 1;
  }

  private setTotalFieldsVisibilityByNumberOfBtls(): void {
    if (this.isLoanGroupTypeBtlAffordability) {
      this.isTotalMonthlyRentalIncomeBtlVisible =
        this.mortgageApplication.HowManyBtlInTotal > 0 || this.mortgageApplication.HowManyBtlWithUs > 1;
      this.isTotalBalanceOutstandingBtlMortgageVisible = this.mortgageApplication.HowManyBtlInTotal > 0;
    }
  }

  public deleteOutgoing(outgoing: OutstandingDebt, index: number): void {
    this.outgoings.splice(index, 1);
    if (outgoing.OutstandingDebtID < 0) {
      this.mortgageApplication.Applicants.forEach((a) =>
        a.OutstandingDebts.forEach(
          (o, i) => o.OutstandingDebtID === outgoing.OutstandingDebtID && a.OutstandingDebts.splice(i, 1)
        )
      );
    } else {
      this.mortgageApplication.Applicants.forEach((a) =>
        a.OutstandingDebts.filter((o) => o.OutstandingDebtID === outgoing.OutstandingDebtID).forEach(
          (o) => (o.IsDeleted = true)
        )
      );
    }
    this.hasOutgoings = this.outgoings.length > 0;
  }

  public get outgoingsTotal(): number {
    return this.hasOutgoings
      ? this.outgoings
          .filter(
            (o) =>
              o.MortgagePayOffCode !== ReferenceDataOptionLists.AnswerTypeYes &&
              o.IsPayedOffWithMortgage !== ReferenceDataOptionLists.AnswerTypeYes &&
              o.MonthlyPayment
          )
          .reduce((sum, o) => sum + o.MonthlyPayment, 0)
      : 0;
  }

  ngOnInit(): void {
    this.cannotSelectOutgoingType =
      !!this.mortgageApplication.HasOutgoingsCode &&
      this.mortgageApplication.HasOutgoingsCode === ReferenceDataOptionLists.AnswerTypeNo;
    this.hasOutgoings = this.outgoings.length > 0;
    this.setBtlAdditionalFieldsVisibility();
    this.setTotalFieldsVisibilityByNumberOfBtls();
  }

  updateCannotAddAndSelectOutgoings(): void {
    this.cannotSelectOutgoingType =
      !!this.mortgageApplication.HasOutgoingsCode &&
      this.mortgageApplication.HasOutgoingsCode === ReferenceDataOptionLists.AnswerTypeNo;
    this.cannotAddOutgoing = this.cannotSelectOutgoingType || !this.selectedOutgoingType;
    this.hasOutgoings = this.outgoings.length > 0;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.mortgageApplication) {
      this.outgoings = new Array<OutstandingDebt>();
      this.mortgageApplication.Applicants.forEach((applicant) =>
        applicant.OutstandingDebts.filter((o) => !o.IsDeleted).forEach((applicantOutgoing) => {
          if (
            !this.outgoings.length ||
            !this.outgoings.some(
              (o) =>
                o.OutstandingDebtID === applicantOutgoing.OutstandingDebtID &&
                (applicantOutgoing = o) &&
                applicantOutgoing.ApplicantOrderIds.push(applicant.ApplicantTransactOrder) > 0
            )
          ) {
            this.outgoings.push(applicantOutgoing);
            applicantOutgoing.ApplicantOrderIds.push(applicant.ApplicantTransactOrder);
          }
        })
      );

      this.mortgageApplication.Applicants.forEach((applicant) => {
        applicant.OutstandingDebts.sort((o1, o2) => (new Date(o1.CreatedDate) > new Date(o2.CreatedDate) ? 1 : -1));
        for (let i = 0; i < applicant.OutstandingDebts.length; i++) {
          const outgoing = applicant.OutstandingDebts[i];
          if (!outgoing.IsDeleted) {
            const outgoingWithApplicantsReferences = this.outgoings.filter(
              (o) => o.OutstandingDebtID === outgoing.OutstandingDebtID
            )[0];
            applicant.OutstandingDebts.splice(i, 1);
            applicant.OutstandingDebts.push(outgoingWithApplicantsReferences);
          }
        }
      });

      this.outgoings.sort((o1, o2) => (new Date(o1.CreatedDate) > new Date(o2.CreatedDate) ? 1 : -1));
    }
  }

  public onHowManyBtlChanged(): void {
    this.setBtlAdditionalFieldsVisibility();
    this.setTotalFieldsVisibilityByNumberOfBtls();
  }

  public ngOnDestroy(): void {
    this.mortgageApplication.Applicants.forEach((applicant) =>
      applicant.OutstandingDebts.filter((o) => !o.IsDeleted).forEach((applicantOutgoing) => {
        applicantOutgoing.ApplicantOrderIds = [];
      })
    );
  }
}
